/**
 * Gartner Demo mock data — all mock constants for the GartnerDemo feature flag.
 * Delete this file and all imports to remove the demo.
 */
import { DescriptorType, FieldListObject } from '@fabric-ui/components';
import { FieldType, FieldTypeEnum } from '../Sources/Mapping/types';
import { EntityBuildingBlock, EntityWeight, ProjectionToBaseScore } from './types';

// DescriptorType numeric values used as fieldMap .type — these are the keys typeLabelMap resolves.
// Must be numeric DescriptorType (not FieldType strings) to match real fieldMap entries.
// Cast needed because FieldTypeEnum reverse-maps return string | DescriptorType.
const BOOL_TYPE = FieldTypeEnum[FieldType.Boolean] as DescriptorType; // TYPE_BOOL = 8
const NUM_TYPE = FieldTypeEnum[FieldType.Number] as DescriptorType; // TYPE_DOUBLE = 1

// ---------------------------------------------------------------------------
// Field stubs — injected into fieldMap and aggProjs[proj].fieldList so that
// ScoringEditFactor can resolve .type/.title and populate the field dropdown.
// Typed as FieldListObject (with required fields as any) so the merged fieldMap
// stays compatible with Record<string, FieldListObject>.
// ---------------------------------------------------------------------------

const mkFieldStub = (
  fieldName: string,
  type: DescriptorType,
  title: string,
  displayName: string,
  group: string,
  projection: string
): FieldListObject =>
  ({
    id: fieldName,
    value: fieldName,
    name: fieldName,
    systemName: fieldName,
    title,
    displayName,
    group,
    type,
    builtIn: true,
    pathAlias: projection,
    mainProjId: { name: projection, builtIn: true },
    entityTypeId: { name: projection, builtIn: true },
    repeated: false,
    tags: [],
    originalType: {},
    visibilityConfig: {
      projectionName: projection,
      builtInProjection: true,
      fieldName,
      config: { hidden: false },
    },
  }) as unknown as FieldListObject;

// Each key is the fieldName; value is a full FieldListObject stub.
// mkFieldStub fills all required FieldListObject fields so the merged fieldMap
// stays typed as Record<string, FieldListObject> without TS errors.
export const GARTNER_MOCK_FIELD_STUBS: Record<string, FieldListObject> = {
  'finding.vulnerability_in_wild': mkFieldStub(
    'finding.vulnerability_in_wild',
    BOOL_TYPE,
    'Vulnerability seen in the wild',
    'Vulnerability seen in the wild',
    'Finding',
    'uber_findings'
  ),
  'finding.exploit_on_decoy': mkFieldStub(
    'finding.exploit_on_decoy',
    BOOL_TYPE,
    'Exploit seen on a decoy',
    'Exploit seen on a decoy',
    'Finding',
    'uber_findings'
  ),
  'finding.involved_in_ransomware': mkFieldStub(
    'finding.involved_in_ransomware',
    BOOL_TYPE,
    'Involved in ransomware',
    'Involved in ransomware',
    'Finding',
    'uber_findings'
  ),
  'finding.active_exploitation': mkFieldStub(
    'finding.active_exploitation',
    BOOL_TYPE,
    'Active exploitation',
    'Active exploitation',
    'Finding',
    'uber_findings'
  ),
  'finding.exploit_maturity_score': mkFieldStub(
    'finding.exploit_maturity_score',
    NUM_TYPE,
    'Exploit Maturity Score',
    'Exploit Maturity Score',
    'Finding',
    'uber_findings'
  ),
  'finding.mitigated_by_zpa': mkFieldStub(
    'finding.mitigated_by_zpa',
    BOOL_TYPE,
    'Mitigated by ZPA',
    'Mitigated by ZPA',
    'Finding',
    'uber_findings'
  ),
  'finding.mitigated_by_zia': mkFieldStub(
    'finding.mitigated_by_zia',
    BOOL_TYPE,
    'Mitigated by ZIA',
    'Mitigated by ZIA',
    'Finding',
    'uber_findings'
  ),
  'finding.original_severity_score': mkFieldStub(
    'finding.original_severity_score',
    NUM_TYPE,
    'Finding Original Severity Score',
    'Original Severity Score',
    'Finding',
    'uber_findings'
  ),
  'finding.cvss_base_score': mkFieldStub(
    'finding.cvss_base_score',
    NUM_TYPE,
    'Vulnerability CVSS Base Score',
    'Vulnerability CVSS Base Score',
    'Finding',
    'uber_findings'
  ),
  'finding.known_exploited': mkFieldStub(
    'finding.known_exploited',
    BOOL_TYPE,
    'Known Exploited',
    'Known Exploited',
    'Finding',
    'uber_findings'
  ),
  'finding.has_exploit': mkFieldStub('finding.has_exploit', BOOL_TYPE, 'Has Exploit', 'Has Exploit', 'Finding', 'uber_findings'),
  'finding.epss_score': mkFieldStub(
    'finding.epss_score',
    NUM_TYPE,
    'Vulnerability EPSS Score',
    'Vulnerability EPSS Score',
    'Finding',
    'uber_findings'
  ),
  'asset.is_crown_jewel': mkFieldStub('asset.is_crown_jewel', BOOL_TYPE, 'Is Crown Jewel', 'Is Crown Jewel', 'Asset', 'uber_assets'),
  'asset.environment': mkFieldStub('asset.environment', NUM_TYPE, 'Environment', 'Environment', 'Asset', 'uber_assets'),
  'asset.has_pii_new': mkFieldStub('asset.has_pii_new', BOOL_TYPE, 'Has PII', 'Has PII', 'Asset', 'uber_assets'),
  'asset.is_publicly_accessible': mkFieldStub(
    'asset.is_publicly_accessible',
    BOOL_TYPE,
    'Is Publicly Accessible',
    'Is Publicly Accessible',
    'Asset',
    'uber_assets'
  ),
  'asset.business_criticality': mkFieldStub(
    'asset.business_criticality',
    NUM_TYPE,
    'Business Criticality',
    'Business Criticality',
    'Asset',
    'uber_assets'
  ),
  'asset.has_pii': mkFieldStub('asset.has_pii', BOOL_TYPE, 'Asset has PII', 'Asset has PII', 'Asset', 'uber_assets'),
  'policy_violation.severity': mkFieldStub(
    'policy_violation.severity',
    NUM_TYPE,
    'Policy Violation Severity',
    'Policy Violation Severity',
    'Policy Violation',
    'uber_policy_violations'
  ),
  'alert.severity': mkFieldStub('alert.severity', NUM_TYPE, 'Alert Severity', 'Alert Severity', 'Alert', 'uber_alerts'),
  'identity_finding.severity': mkFieldStub(
    'identity_finding.severity',
    NUM_TYPE,
    'Identity Finding Severity',
    'Identity Finding Severity',
    'Identity Finding',
    'uber_identity_findings'
  ),
  'user.risk_score': mkFieldStub('user.risk_score', NUM_TYPE, 'User Risk Score', 'User Risk Score', 'User', 'uber_users'),
  'tenant.risk_score': mkFieldStub('tenant.risk_score', NUM_TYPE, 'Tenant Risk Score', 'Tenant Risk Score', 'Tenant', 'uber_tenants'),
};

// Grouped by projection — used only by buildGartnerAccountEntities below.
const GARTNER_MOCK_FIELD_LIST_ENTRIES: Record<string, FieldListObject[]> = Object.entries(GARTNER_MOCK_FIELD_STUBS).reduce(
  (acc, [, stub]) => {
    const proj = stub.pathAlias;
    if (!acc[proj]) acc[proj] = [];
    acc[proj].push(stub);
    return acc;
  },
  {} as Record<string, FieldListObject[]>
);

/**
 * Merges Gartner mock field stubs into a real accountEntities object.
 * Pass the result as `accountEntitiesOverride` to ScoringEditFactor.
 *   - fieldMap: mock stubs win over real entries (correct type/title for mock fields)
 *   - aggProjs[proj].fieldList.*: mock fields prepended so they appear in the field dropdown
 */
export const buildGartnerAccountEntities = <T extends { aggProjs: Record<string, any>; fieldMap: Record<string, FieldListObject> }>(
  baseEntities: T
): T => {
  const mergedAggProjs = Object.fromEntries(
    Object.entries(baseEntities.aggProjs).map(([projName, proj]) => {
      const mockEntries = GARTNER_MOCK_FIELD_LIST_ENTRIES[projName];
      if (!mockEntries) return [projName, proj];
      const mergedFieldList = { ...proj.fieldList };
      Object.keys(mergedFieldList).forEach(key => {
        mergedFieldList[key] = [...mockEntries, ...mergedFieldList[key]];
      });
      return [projName, { ...proj, fieldList: mergedFieldList }];
    })
  ) as T['aggProjs'];
  return {
    ...baseEntities,
    fieldMap: { ...baseEntities.fieldMap, ...GARTNER_MOCK_FIELD_STUBS } as T['fieldMap'],
    aggProjs: mergedAggProjs,
  };
};

// ---------------------------------------------------------------------------
// Base scores (factors per projection)
// ---------------------------------------------------------------------------

const mkBoolFactor = (fieldName: string, displayName: string, trueScore: number, falseScore: number) => ({
  fieldName,
  displayName,
  fallback: falseScore,
  scoringRules: [
    { value: trueScore, filter: { expression: { fieldName, boolCondition: { equals: true } } } },
    { value: falseScore, filter: { expression: { fieldName, boolCondition: { equals: false } } } },
  ],
});

const mkNumberFactor = (fieldName: string, displayName: string, score: number) => ({
  fieldName,
  displayName,
  fallback: 0,
  scoringRules: [{ value: score, filter: null, adjustmentType: 'FIELD_BASED' as const }],
});

export const GARTNER_MOCK_BASE_SCORES: ProjectionToBaseScore[] = [
  {
    projectionName: 'uber_findings',
    baseScore: {
      id: null,
      accountId: '',
      projectionName: 'uber_findings',
      config: {
        configTypes: [
          {
            type: 'CVE',
            isDefault: true,
            factorGroups: [
              {
                name: 'Exploitability',
                weight: 0.6,
                factors: [
                  mkBoolFactor('finding.vulnerability_in_wild', 'Vulnerability seen in the wild', 15, 0),
                  mkBoolFactor('finding.exploit_on_decoy', 'Exploit seen on a decoy', 20, 0),
                  mkBoolFactor('finding.involved_in_ransomware', 'Involved in ransomware', 10, 0),
                  mkBoolFactor('finding.active_exploitation', 'Active exploitation', 20, 0),
                  mkBoolFactor('finding.known_exploited', 'Known Exploited', 20, 0),
                  mkBoolFactor('finding.has_exploit', 'Has Exploit', 20, 0),
                  mkNumberFactor('finding.epss_score', 'EPSS Score', 10),
                  mkNumberFactor('finding.exploit_maturity_score', 'Exploit Maturity Score', 5),
                ],
              },
              {
                name: 'Exposure',
                weight: 0.4,
                factors: [
                  mkNumberFactor('finding.cvss_base_score', 'CVSS Base Score', 10),
                  mkBoolFactor('finding.mitigated_by_zpa', 'Mitigated by ZPA', -10, 0),
                  mkBoolFactor('finding.mitigated_by_zia', 'Mitigated by ZIA', -10, 0),
                ],
              },
            ],
          },
          {
            type: 'Misconfig',
            isDefault: false,
            factorGroups: [
              {
                name: 'DEFAULT',
                weight: 1,
                factors: [
                  {
                    fieldName: 'finding.original_severity_score',
                    displayName: 'Original Severity Score',
                    fallback: 0,
                    scoringRules: [{ value: 1, filter: null, adjustmentType: 'FIELD_BASED' as const }],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_policy_violations',
    baseScore: {
      id: null,
      accountId: '',
      projectionName: 'uber_policy_violations',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [
              {
                name: 'DEFAULT',
                weight: 1,
                factors: [mkNumberFactor('policy_violation.severity', 'Policy Violation Severity', 1)],
              },
            ],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_alerts',
    baseScore: {
      id: null,
      accountId: '',
      projectionName: 'uber_alerts',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [
              {
                name: 'DEFAULT',
                weight: 1,
                factors: [mkNumberFactor('alert.severity', 'Alert Severity', 1)],
              },
            ],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_identity_findings',
    baseScore: {
      id: 'u1o1m',
      accountId: '5llzfe',
      projectionName: 'uber_identity_findings',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [
              {
                name: 'Identity Finding Risk',
                weight: 1,
                factors: [
                  {
                    displayName: null,
                    fieldName: 'identity_finding.first_seen',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 15,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 86, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 14,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 79, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 13,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 72, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 12,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 65, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 11,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 58, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 10,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 51, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 9,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 44, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 8,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 37, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 7,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.first_seen',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 30, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'identity_finding.severity_score',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 100,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 100 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 90,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 90 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 80,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 80 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 70,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 70 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 60,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 60 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 50,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 50 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 40,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 40 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 30,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 30 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 20,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 20 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 10,
                        filter: { expression: { fieldName: 'identity_finding.severity_score', numberCondition: { gte: 10 } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'identity_finding.exploit_ease',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 5,
                        filter: {
                          expression: {
                            fieldName: 'identity_finding.exploit_ease',
                            stringCondition: { equals: 'easy', addNullFilter: false },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_users',
    baseScore: {
      id: 'mi1zrw',
      accountId: '5llzfe',
      projectionName: 'uber_users',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [
              {
                name: 'User Risk',
                weight: 1,
                factors: [
                  {
                    displayName: null,
                    fieldName: 'user.last_activity_date',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 25,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 115, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 24,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 108, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 23,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 101, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 22,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 94, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 21,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 87, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 20,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 80, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 19,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 73, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 18,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 66, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 17,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 59, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 16,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 52, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 15,
                        filter: {
                          expression: {
                            fieldName: 'user.last_activity_date',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 45, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.is_admin',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 30,
                        filter: { expression: { fieldName: 'user.is_admin', boolCondition: { equals: true } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.type',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 10,
                        filter: { expression: { fieldName: 'user.type', stringCondition: { equals: 'external', addNullFilter: false } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.identity_type',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 10,
                        filter: {
                          expression: {
                            fieldName: 'user.identity_type',
                            stringCondition: { equals: 'ServicePrincipal', addNullFilter: false },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.account_enabled',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: -5,
                        filter: { expression: { fieldName: 'user.account_enabled', boolCondition: { equals: false } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.password_changed',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 20,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 130, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 19,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 123, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 18,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 116, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 17,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 109, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 16,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 102, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 15,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 95, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 14,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 88, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 13,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 81, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 12,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 74, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 11,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 67, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                      {
                        value: 10,
                        filter: {
                          expression: {
                            fieldName: 'user.password_changed',
                            dateCondition: {
                              relative: { unit: 'DAYS', value: 60, direction: 'UNKNOWN_DIRECTION', periodBehaviour: 'BEFORE' },
                            },
                          },
                        },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.status',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: -5,
                        filter: { expression: { fieldName: 'user.status', stringCondition: { notEqual: 'active', addNullFilter: false } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.mfa_enabled',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 25,
                        filter: { expression: { fieldName: 'user.mfa_enabled', boolCondition: { equals: false } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                  {
                    displayName: null,
                    fieldName: 'user.is_privileged',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 20,
                        filter: { expression: { fieldName: 'user.is_privileged', boolCondition: { equals: true } } },
                        adjustmentType: 'MANUAL' as const,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_tenants',
    baseScore: {
      id: 'j4ci4x',
      accountId: '5llzfe',
      projectionName: 'uber_tenants',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_assets',
    baseScore: {
      id: null,
      accountId: '',
      projectionName: 'uber_assets',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [
              {
                name: 'DEFAULT',
                weight: 1,
                factors: [
                  {
                    fieldName: 'asset.is_crown_jewel',
                    displayName: 'Is Crown Jewel',
                    fallback: 0,
                    scoringRules: [
                      { value: 30, filter: { expression: { fieldName: 'asset.is_crown_jewel', boolCondition: { equals: true } } } },
                      { value: 0, filter: { expression: { fieldName: 'asset.is_crown_jewel', boolCondition: { equals: false } } } },
                    ],
                  },
                  {
                    fieldName: 'asset.environment',
                    displayName: 'Environment',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 15,
                        filter: { expression: { fieldName: 'asset.environment', stringCondition: { startsWith: 'prod' } } },
                      },
                      { value: 0, filter: null, adjustmentType: 'FIELD_BASED' as const },
                    ],
                  },
                  {
                    fieldName: 'asset.has_pii_new',
                    displayName: 'Has PII',
                    fallback: 0,
                    scoringRules: [
                      { value: 15, filter: { expression: { fieldName: 'asset.has_pii_new', boolCondition: { equals: true } } } },
                      { value: 0, filter: { expression: { fieldName: 'asset.has_pii_new', boolCondition: { equals: false } } } },
                    ],
                  },
                  {
                    fieldName: 'asset.is_publicly_accessible',
                    displayName: 'Is Publicly Accessible',
                    fallback: 0,
                    scoringRules: [
                      {
                        value: 20,
                        filter: { expression: { fieldName: 'asset.is_publicly_accessible', boolCondition: { equals: true } } },
                      },
                      {
                        value: 0,
                        filter: { expression: { fieldName: 'asset.is_publicly_accessible', boolCondition: { equals: false } } },
                      },
                    ],
                  },
                  {
                    fieldName: 'asset.business_criticality',
                    displayName: 'Business Criticality',
                    fallback: 0,
                    scoringRules: [
                      { value: 0, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 1 } } } },
                      { value: 5, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 2 } } } },
                      { value: 10, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 3 } } } },
                      { value: 20, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 4 } } } },
                      { value: 30, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 5 } } } },
                    ],
                  },
                  mkBoolFactor('asset.has_pii', 'Asset has PII', 15, 0),
                ],
              },
            ],
          },
        ],
      },
    },
  },
];

// ---------------------------------------------------------------------------
// Building blocks (left-panel factor list)
// ---------------------------------------------------------------------------

export const GARTNER_MOCK_BUILDING_BLOCKS: EntityBuildingBlock[] = [
  {
    projectionId: 'uber_assets',
    name: 'Asset Business Context',
    activeCount: 6,
    activeLabel: 'Factors',
    factors: [
      { name: 'asset.is_crown_jewel', displayName: 'Is Crown Jewel' },
      { name: 'asset.environment', displayName: 'Environment' },
      { name: 'asset.has_pii_new', displayName: 'Has PII' },
      { name: 'asset.is_publicly_accessible', displayName: 'Is Publicly Accessible' },
      { name: 'asset.business_criticality', displayName: 'Business Criticality' },
      { name: 'asset.has_pii', displayName: 'Asset has PII' },
    ],
    configTypes: GARTNER_MOCK_BASE_SCORES[6].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_findings',
    name: 'Finding',
    activeCount: 11,
    activeLabel: 'Factors',
    factors: [
      { name: 'finding.vulnerability_in_wild', displayName: 'Vulnerability seen in the wild' },
      { name: 'finding.exploit_on_decoy', displayName: 'Exploit seen on a decoy' },
      { name: 'finding.involved_in_ransomware', displayName: 'Involved in ransomware' },
      { name: 'finding.active_exploitation', displayName: 'Active exploitation' },
      { name: 'finding.known_exploited', displayName: 'Known Exploited' },
      { name: 'finding.has_exploit', displayName: 'Has Exploit' },
      { name: 'finding.epss_score', displayName: 'EPSS Score' },
      { name: 'finding.exploit_maturity_score', displayName: 'Exploit Maturity Score' },
      { name: 'finding.mitigated_by_zpa', displayName: 'Mitigated by ZPA' },
      { name: 'finding.mitigated_by_zia', displayName: 'Mitigated by ZIA' },
      { name: 'finding.cvss_base_score', displayName: 'CVSS Base Score' },
    ],
    configTypes: GARTNER_MOCK_BASE_SCORES[0].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_policy_violations',
    name: 'Policy Violation Severity',
    activeCount: 0,
    activeLabel: 'Active Policies',
    factors: [],
    configTypes: GARTNER_MOCK_BASE_SCORES[1].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_alerts',
    name: 'Alert',
    activeCount: 1,
    activeLabel: 'Factors',
    factors: [{ name: 'alert.severity', displayName: 'Alert Severity' }],
    configTypes: GARTNER_MOCK_BASE_SCORES[2].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_users',
    name: 'User',
    activeCount: 9,
    activeLabel: 'Factors',
    factors: [
      { name: 'user.last_activity_date', displayName: 'Last Activity Date' },
      { name: 'user.is_admin', displayName: 'Is Admin' },
      { name: 'user.type', displayName: 'Type' },
      { name: 'user.identity_type', displayName: 'Identity Type' },
      { name: 'user.account_enabled', displayName: 'Account Enabled' },
      { name: 'user.password_changed', displayName: 'Password Changed' },
      { name: 'user.status', displayName: 'Status' },
      { name: 'user.mfa_enabled', displayName: 'MFA Enabled' },
      { name: 'user.is_privileged', displayName: 'Is Privileged' },
    ],
    configTypes: GARTNER_MOCK_BASE_SCORES[4].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_identity_findings',
    name: 'Identity Finding',
    activeCount: 3,
    activeLabel: 'Factors',
    factors: [
      { name: 'identity_finding.first_seen', displayName: 'First Seen' },
      { name: 'identity_finding.severity_score', displayName: 'Severity Score' },
      { name: 'identity_finding.exploit_ease', displayName: 'Exploit Ease' },
    ],
    configTypes: GARTNER_MOCK_BASE_SCORES[3].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_tenants',
    name: 'Tenant',
    activeCount: 0,
    activeLabel: 'Factors',
    factors: [],
    configTypes: GARTNER_MOCK_BASE_SCORES[5].baseScore.config.configTypes,
  },
];

// ---------------------------------------------------------------------------
// Entity weights (weight contribution panel)
// ---------------------------------------------------------------------------

export const GARTNER_MOCK_ENTITY_WEIGHTS: EntityWeight[] = [
  { projectionId: 'uber_assets', name: 'Asset Business Context', weight: 30, factorCount: 6, allowedAggTypes: [], hasAggregation: false },
  {
    projectionId: 'uber_findings',
    name: 'Finding',
    weight: 30,
    factorCount: 11,
    allowedAggTypes: ['AVG', 'MAX'],
    hasAggregation: true,
    aggregation: 'AVG',
  },
  {
    projectionId: 'uber_policy_violations',
    name: 'Policy Violation Severity',
    weight: 20,
    factorCount: 0,
    allowedAggTypes: ['AVG', 'MAX'],
    hasAggregation: true,
    aggregation: 'AVG',
  },
  {
    projectionId: 'uber_alerts',
    name: 'Alert',
    weight: 20,
    factorCount: 1,
    allowedAggTypes: ['AVG', 'MAX'],
    hasAggregation: true,
    aggregation: 'AVG',
  },
  {
    projectionId: 'uber_identity_findings',
    name: 'Identity Finding',
    weight: 0,
    factorCount: 3,
    allowedAggTypes: ['AVG', 'MAX'],
    hasAggregation: true,
    aggregation: 'AVG',
  },
  {
    projectionId: 'uber_users',
    name: 'User',
    weight: 0,
    factorCount: 9,
    allowedAggTypes: ['AVG', 'MAX'],
    hasAggregation: true,
    aggregation: 'AVG',
  },
  {
    projectionId: 'uber_tenants',
    name: 'Tenant',
    weight: 0,
    factorCount: 0,
    allowedAggTypes: ['AVG', 'MAX'],
    hasAggregation: true,
    aggregation: 'AVG',
  },
];
